import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import COMPANY_INFO from '../../config/companyInfo';
import api from '../../services/api';

// Detect shipment type from URL path
function useShipmentType() {
  const { pathname } = useLocation();
  if (pathname.includes('/air-import/')) return 'air-import';
  if (pathname.includes('/ocean-import/')) return 'ocean-import';
  if (pathname.includes('/ocean-export/')) return 'ocean-export';
  if (pathname.includes('/misc/')) return 'misc';
  return 'truck';
}

// Normalize different shipment types to common field names for DO rendering
function normalizeShipment(raw, type) {
  if (type === 'air-import') {
    const h = (raw.hawbs && raw.hawbs[0]) || {};
    return {
      file_no: raw.file_no,
      post_date: raw.post_date,
      mbl_no: raw.mawb_no || '',
      hbl_no: h.hawb_no || '',
      vessel_flight_no: raw.flight_no || '',
      trucker: raw.trucker || h.trucker || '',
      customer: h.customer || '',
      consignee: h.consignee || '',
      shipper: h.shipper || '',
      bill_to: h.bill_to || '',
      freight_pickup: h.freight_location || raw.freight_location || '',
      empty_pickup: '',
      delivery_to: h.delivery_location || '',
      empty_return: '',
      eta: raw.arrival_date_time || '',
      etd: raw.departure_date_time || '',
      port_of_loading: raw.departure || '',
      port_of_discharge: raw.destination || '',
      final_destination: h.final_destination || raw.destination || '',
      package_count: raw.package_count || h.package_count || '',
      package_type: raw.pkg_type || h.pkg_type || '',
      weight_kg: raw.gross_weight_kg || h.gross_weight_kg || '',
      weight_lb: raw.gross_weight_lb || h.gross_weight_lb || '',
      measurement_cbm: raw.measurement_cbm || h.measurement_cbm || '',
      memo: raw.memo || h.memo || '',
      description: raw.description || '',
      created_at: raw.created_at,
    };
  }
  if (type === 'ocean-import') {
    const h = (raw.hbls && raw.hbls[0]) || {};
    // Aggregate weight/cbm/packages across all HBLs
    const hbls = raw.hbls || [];
    const totalPkg = hbls.reduce((s, hb) => s + (Number(hb.package_count) || 0), 0);
    const totalWt = hbls.reduce((s, hb) => s + (Number(hb.weight_kg) || 0), 0);
    const totalCbm = hbls.reduce((s, hb) => s + (Number(hb.measurement_cbm) || 0), 0);
    return {
      file_no: raw.file_no,
      post_date: raw.post_date,
      mbl_no: raw.mbl_no || '',
      hbl_no: h.hbl_no || '',
      vessel_flight_no: [raw.vessel, raw.voyage].filter(Boolean).join(' / ') || '',
      trucker: h.trucker || '',
      consignee: h.consignee || '',
      shipper: h.shipper || '',
      bill_to: h.bill_to || '',
      freight_location: raw.freight_location || h.cy_cfs_location || raw.cfs_location || '',
      delivery_to: h.delivery_location || '',
      empty_return: '',
      eta: raw.eta || '',
      etd: raw.etd || '',
      port_of_loading: raw.port_of_loading || '',
      port_of_discharge: raw.port_of_discharge || '',
      final_destination: raw.final_destination || h.final_destination || '',
      package_count: totalPkg || h.package_count || '',
      package_type: 'CARTON(S)',
      weight_kg: totalWt || '',
      measurement_cbm: totalCbm || '',
      memo: raw.memo || h.memo || '',
      description: raw.description || '',
      created_at: raw.created_at,
    };
  }
  // Truck / misc — fields already match
  return raw;
}

const DeliveryOrder = () => {
  const { id } = useParams();
  const shipmentType = useShipmentType();
  const contentRef = useRef(null);

  const [shipment, setShipment] = useState(null);
  const [truckerInfo, setTruckerInfo] = useState(null);
  const [pickupInfo, setPickupInfo] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchShipmentData();
  }, [id, shipmentType]);

  const fetchTP = async (name) => {
    if (!name) return null;
    try {
      const res = await api.get(`/trade-partners/search?q=${encodeURIComponent(name)}`);
      if (Array.isArray(res.data) && res.data.length > 0) return res.data[0];
    } catch {}
    return null;
  };

  const fetchShipmentData = async () => {
    try {
      setLoading(true);
      const apiPath = `/${shipmentType}/${id}`;
      const shipmentRes = await api.get(apiPath);
      const normalized = normalizeShipment(shipmentRes.data, shipmentType);
      setShipment(normalized);

      // Fetch containers (truck/ocean types)
      if (shipmentType === 'truck' || shipmentType.includes('ocean')) {
        try {
          const contRes = await api.get(`/containers/shipment/${id}`);
          let cts = Array.isArray(contRes.data) ? contRes.data : [];
          // If no container records exist for ocean-import, build from HBL data
          if (cts.length === 0 && shipmentType === 'ocean-import') {
            const hbls = shipmentRes.data.hbls || [];
            // Also check MBL-level container_qty
            const mblQty = shipmentRes.data.container_qty || '';
            const allSources = hbls.length > 0 ? hbls : (mblQty ? [{ container_qty: mblQty }] : []);
            const synth = [];
            for (const hb of allSources) {
              const qty = hb.container_qty || mblQty || '';
              // Parse "1x40HQ", "2x20GP", "1X40HC", etc.
              const m = qty.match(/(\d+)\s*[xX×]\s*(\S+)/);
              const count = m ? parseInt(m[1], 10) : 1;
              const size = m ? m[2] : qty;
              const wt = Number(hb.weight_kg) || 0;
              const cbm = Number(hb.measurement_cbm) || 0;
              const pkgs = Number(hb.package_count) || 0;
              for (let i = 0; i < count; i++) {
                synth.push({
                  container_no: '',
                  seal_no: '',
                  tp_sz: size || '',
                  pkg_count: count > 1 ? '' : (pkgs || ''),
                  pkg_type: 'CARTON(S)',
                  weight_kg: count > 1 ? '' : (wt || ''),
                  measurement_cbm: count > 1 ? '' : (cbm || ''),
                });
              }
            }
            if (synth.length > 0) cts = synth;
          }
          setContainers(cts);
        } catch { setContainers([]); }
      }

      // Fetch trade partner details
      const [trucker, pickup, delivery] = await Promise.all([
        fetchTP(normalized.trucker),
        fetchTP(normalized.freight_location || normalized.freight_pickup || normalized.empty_pickup),
        fetchTP(normalized.delivery_to),
      ]);
      setTruckerInfo(trucker);
      setPickupInfo(pickup);
      setDeliveryInfo(delivery);

      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading shipment data...</div>;
  if (error) return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  if (!shipment) return <div style={{ padding: '20px' }}>No shipment data found</div>;

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  const getAddressBlock = (info) => {
    if (!info) return null;
    const cityStateZip = [
      [info.city, info.state].filter(Boolean).join(', '),
      info.zip_code
    ].filter(Boolean).join(' ');
    return (
      <div style={{ fontSize: '9px', lineHeight: '1.5' }}>
        {info.address && <div>{info.address}</div>}
        {cityStateZip && <div>{cityStateZip}</div>}
        {info.tel && <div>Tel: {info.tel}</div>}
      </div>
    );
  };

  // Aggregate totals from containers
  const totalPkgs = containers.reduce((s, ct) => s + (Number(ct.pkg_count) || 0), 0) || shipment.package_count || '';
  const totalWtKg = containers.reduce((s, ct) => s + (Number(ct.weight_kg) || 0), 0) || shipment.weight_kg || '';
  const totalWtLb = containers.reduce((s, ct) => s + (Number(ct.weight_lb) || 0), 0) || shipment.weight_lb || '';
  const totalCBM = containers.reduce((s, ct) => s + (Number(ct.measurement_cbm) || 0), 0) || shipment.measurement_cbm || '';
  const pkgType = shipment.package_type || shipment.pkg_type || 'CARTON(S)';
  const isAir = shipmentType === 'air-import';
  const pickupLabel = (isAir || shipmentType.includes('ocean')) ? 'FREIGHT LOCATION' : 'PICKUP';

  const fmtNum = (v) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(undefined, { maximumFractionDigits: 2 }); };
  const weightStr = totalWtLb ? `${fmtNum(totalWtLb)} lbs` : totalWtKg ? `${fmtNum(totalWtKg)} kg` : '';

  const cellStyle = (extra = {}) => ({ padding: '8px 10px', borderRight: '1px solid #000', ...extra });
  const labelStyle = { fontWeight: 'bold', fontSize: '9px', color: '#333', marginBottom: '3px' };
  const valueStyle = { fontSize: '11px' };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <DocumentToolbar
        title="Delivery Order"
        onClose={() => window.close()}
        contentRef={contentRef}
        pdfUrl={`/pdf/shipment/${shipmentType}/${id}/delivery-order`}
        fileName={`DO-${shipment.file_no || id}.pdf`}
      />

      <div
        ref={contentRef}
        style={{
          backgroundColor: 'white', padding: '40px', marginTop: '20px',
          marginLeft: 'auto', marginRight: 'auto', maxWidth: '850px',
          boxShadow: '0 0 10px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif',
          fontSize: '11px', lineHeight: '1.4', color: '#000',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #000' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {COMPANY_INFO.logo && <img src={COMPANY_INFO.logo} alt="" style={{ height: '52px', marginRight: '12px' }} />}
              <div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>{COMPANY_INFO.name}</div>
              <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
              <div>{COMPANY_INFO.address}</div>
              <div>{COMPANY_INFO.cityStateZip}</div>
              <div>TEL: {COMPANY_INFO.tel}</div>
              <div>Email: {COMPANY_INFO.email}</div>
            </div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', paddingTop: '10px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', letterSpacing: '2px' }}>DELIVERY ORDER</div>
          </div>
        </div>

        {/* Subtitle */}
        <div style={{ marginBottom: '20px', fontSize: '10px', fontWeight: 'bold' }}>
          THE MERCHANDISE DESCRIBED BELOW WILL BE ENTERED AND/OR FORWARDED AS FOLLOWS:
        </div>

        {/* Date & File No */}
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
          <div><span style={{ fontWeight: 'bold' }}>Date:</span> {formatDate(shipment.post_date || shipment.created_at)}</div>
          <div><span style={{ fontWeight: 'bold' }}>File No.:</span> {shipment.file_no || ''}</div>
        </div>

        {/* Main Content Table */}
        <div style={{ marginBottom: '10px', border: '1px solid #000' }}>
          {/* Row 1: TRUCKER | MB/L NO. | HB/L NO. */}
          <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '55px' }}>
            <div style={cellStyle({ flex: 1 })}>
              <div style={labelStyle}>TRUCKER</div>
              <div style={valueStyle}>{shipment.trucker || ''}</div>
              {getAddressBlock(truckerInfo)}
            </div>
            <div style={cellStyle({ flex: 1 })}>
              <div style={labelStyle}>{isAir ? 'MAWB NO.' : 'MB/L NO.'}</div>
              <div style={valueStyle}>{shipment.mbl_no || ''}</div>
            </div>
            <div style={cellStyle({ flex: 1, borderRight: 'none' })}>
              <div style={labelStyle}>{isAir ? 'HAWB NO.' : 'HB/L NO.'}</div>
              <div style={valueStyle}>{shipment.hbl_no || ''}</div>
            </div>
          </div>

          {/* Row 2: VESSEL / FLIGHT | QTY (PKG) | ETD | ETA */}
          <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '40px' }}>
            <div style={cellStyle({ flex: 1.2 })}>
              <div style={labelStyle}>{isAir ? 'FLIGHT NO.' : 'VESSEL / FLIGHT'}</div>
              <div style={valueStyle}>{shipment.vessel_flight_no || ''}</div>
            </div>
            <div style={cellStyle({ flex: 0.8 })}>
              <div style={labelStyle}>QTY (PKG)</div>
              <div style={valueStyle}>{totalPkgs ? `${fmtNum(totalPkgs)} ${pkgType}` : ''}</div>
            </div>
            <div style={cellStyle({ flex: 0.5 })}>
              <div style={labelStyle}>ETD</div>
              <div style={valueStyle}>{formatDate(shipment.etd)}</div>
            </div>
            <div style={cellStyle({ flex: 0.5, borderRight: 'none' })}>
              <div style={labelStyle}>ETA</div>
              <div style={valueStyle}>{formatDate(shipment.eta)}</div>
            </div>
          </div>

          {/* Row 3: DEPARTURE | DESTINATION | WEIGHT | CBM */}
          <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '40px' }}>
            <div style={cellStyle({ flex: 1.2 })}>
              <div style={labelStyle}>DEPARTURE</div>
              <div style={valueStyle}>{shipment.port_of_loading || ''}</div>
            </div>
            <div style={cellStyle({ flex: 0.8 })}>
              <div style={labelStyle}>DESTINATION</div>
              <div style={valueStyle}>{shipment.port_of_discharge || shipment.final_destination || ''}</div>
            </div>
            <div style={cellStyle({ flex: 0.5 })}>
              <div style={labelStyle}>WEIGHT</div>
              <div style={valueStyle}>{weightStr}</div>
            </div>
            <div style={cellStyle({ flex: 0.5, borderRight: 'none' })}>
              <div style={labelStyle}>CBM</div>
              <div style={valueStyle}>{totalCBM || ''}</div>
            </div>
          </div>

          {/* Row 4: PICKUP/FREIGHT | DELIVERY TO */}
          <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '45px' }}>
            <div style={cellStyle({ flex: 1 })}>
              <div style={labelStyle}>{pickupLabel}</div>
              <div style={valueStyle}>{shipment.freight_location || shipment.freight_pickup || shipment.empty_pickup || ''}</div>
              {getAddressBlock(pickupInfo)}
            </div>
            <div style={cellStyle({ flex: 1, borderRight: 'none' })}>
              <div style={labelStyle}>DELIVERY TO</div>
              <div style={valueStyle}>{shipment.delivery_to || ''}</div>
              {getAddressBlock(deliveryInfo)}
            </div>
          </div>

          {/* Row 5: EMPTY RETURN | BILL TO */}
          <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '35px' }}>
            <div style={cellStyle({ flex: 1 })}>
              <div style={labelStyle}>EMPTY RETURN TO</div>
              <div style={valueStyle}>{shipment.empty_return || ''}</div>
            </div>
            <div style={cellStyle({ flex: 1, borderRight: 'none' })}>
              <div style={labelStyle}>BILL TO</div>
              <div style={valueStyle}>{shipment.bill_to || COMPANY_INFO.name}</div>
            </div>
          </div>

          {/* Row 6: CONSIGNEE | SHIPPER */}
          <div style={{ display: 'flex', minHeight: '40px' }}>
            <div style={cellStyle({ flex: 1 })}>
              <div style={labelStyle}>CONSIGNEE</div>
              <div style={valueStyle}>{shipment.consignee || ''}</div>
            </div>
            <div style={cellStyle({ flex: 1, borderRight: 'none' })}>
              <div style={labelStyle}>SHIPPER</div>
              <div style={valueStyle}>{shipment.shipper || ''}</div>
            </div>
          </div>
        </div>

        {/* Container List Table */}
        {containers.length > 0 && (
          <div style={{ marginBottom: '10px', border: '1px solid #000' }}>
            {/* Header */}
            <div style={{ display: 'flex', background: '#f3f4f6', borderBottom: '1px solid #000', fontWeight: 'bold', fontSize: '9px', color: '#333' }}>
              <div style={{ flex: 2, padding: '5px 8px', borderRight: '1px solid #ccc' }}>CONTAINER NO.</div>
              <div style={{ flex: 1, padding: '5px 8px', borderRight: '1px solid #ccc' }}>SEAL NO.</div>
              <div style={{ flex: 0.7, padding: '5px 8px', borderRight: '1px solid #ccc' }}>SIZE</div>
              <div style={{ flex: 1.3, padding: '5px 8px', borderRight: '1px solid #ccc' }}>PKG</div>
              <div style={{ flex: 1, padding: '5px 8px', borderRight: '1px solid #ccc' }}>WEIGHT (KG)</div>
              <div style={{ flex: 0.8, padding: '5px 8px' }}>CBM</div>
            </div>
            {/* Rows */}
            {containers.map((ct, i) => (
              <div key={ct.id || i} style={{ display: 'flex', borderBottom: i < containers.length - 1 || containers.length > 1 ? '1px solid #ddd' : 'none', fontSize: '11px' }}>
                <div style={{ flex: 2, padding: '5px 8px', borderRight: '1px solid #eee', fontWeight: '600' }}>{ct.container_no || ''}</div>
                <div style={{ flex: 1, padding: '5px 8px', borderRight: '1px solid #eee' }}>{ct.seal_no || ''}</div>
                <div style={{ flex: 0.7, padding: '5px 8px', borderRight: '1px solid #eee' }}>{ct.tp_sz || ''}</div>
                <div style={{ flex: 1.3, padding: '5px 8px', borderRight: '1px solid #eee' }}>{ct.pkg_count ? `${fmtNum(ct.pkg_count)} ${ct.pkg_type || pkgType}` : ''}</div>
                <div style={{ flex: 1, padding: '5px 8px', borderRight: '1px solid #eee' }}>{ct.weight_kg ? fmtNum(ct.weight_kg) : ''}</div>
                <div style={{ flex: 0.8, padding: '5px 8px' }}>{ct.measurement_cbm || ''}</div>
              </div>
            ))}
            {/* Total row (if multiple containers) */}
            {containers.length > 1 && (
              <div style={{ display: 'flex', borderTop: '1px solid #000', background: '#f3f4f6', fontSize: '10px', fontWeight: 'bold', color: '#333' }}>
                <div style={{ flex: 2, padding: '5px 8px', borderRight: '1px solid #ccc' }}>TOTAL: {containers.length} CONTAINERS</div>
                <div style={{ flex: 1, padding: '5px 8px', borderRight: '1px solid #ccc' }}></div>
                <div style={{ flex: 0.7, padding: '5px 8px', borderRight: '1px solid #ccc' }}></div>
                <div style={{ flex: 1.3, padding: '5px 8px', borderRight: '1px solid #ccc' }}>{totalPkgs ? fmtNum(totalPkgs) : ''}</div>
                <div style={{ flex: 1, padding: '5px 8px', borderRight: '1px solid #ccc' }}>{totalWtKg ? fmtNum(totalWtKg) : ''}</div>
                <div style={{ flex: 0.8, padding: '5px 8px' }}>{totalCBM || ''}</div>
              </div>
            )}
          </div>
        )}

        {/* DESCRIPTION / MEMO */}
        <div style={{ marginBottom: '10px', border: '1px solid #000', padding: '10px', minHeight: '50px' }}>
          <div style={labelStyle}>DESCRIPTION / MEMO</div>
          <div style={{ fontSize: '11px', whiteSpace: 'pre-wrap', marginTop: '3px' }}>{shipment.description || shipment.memo || ''}</div>
        </div>

        {/* Footer Notes */}
        <div style={{ fontSize: '8px', lineHeight: '1.6', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #000' }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>TERMS & CONDITIONS:</div>
          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
            <li>This Delivery Order (DO) certifies the condition and quantity of goods as of the date of pickup.</li>
            <li>Recipient must inspect goods upon delivery and report any discrepancies within 24 hours.</li>
            <li>Shipper is responsible for accurate description and declaration of contents.</li>
            <li>All goods are shipped at the sender's risk unless otherwise agreed in writing.</li>
            <li>Claims for loss or damage must be filed within 30 days of delivery.</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default DeliveryOrder;
